"use client";
import gsap from "gsap";
import "./feedbackSec.css";
import Image from "next/image";
import { Play, Quote } from "lucide-react";
import { IoMdCloseCircle } from "react-icons/io";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useState, useEffect, useRef } from "react";
import StarRating from "src/components/atoms/StarRating";
import FeedbackHeroSection from "src/components/molecules/ClientTestimonialsSections/FeedbackHeroSection";

gsap.registerPlugin(ScrollTrigger);

const ClientTestimonials = ({ textTestimonials }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const [showButtons, setShowButtons] = useState({});
  const textRefs = useRef({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const containerRef = useRef(null);
  useEffect(() => {
    const buttons = {};

    Object.keys(textRefs.current).forEach((key) => {
      const el = textRefs.current[key];

      if (el) {
        buttons[key] = el.scrollHeight > el.clientHeight;
      }
    });

    setShowButtons(buttons);
  }, [textTestimonials]);
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".fade-up", {
        opacity: 0,
        y: 80,
        duration: 1,
        stagger: 0.2,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 80%",
        },
      });
    });

    return () => ctx.revert();
  }, []);
  const glowColors = ["rgba(168,85,247,0.95)", "rgba(59,130,246,0.95)", "rgba(6,182,212,0.95)", "rgba(236,72,153,0.95)"];
  return (
    <div ref={containerRef} className="min-h-screen bg-background-primary  overflow-hidden">
      <FeedbackHeroSection />

      <div className="footer-top-accent" aria-hidden="true" />
      <section className="py-0! md:py-15! !px-6">
        <div className="mx-auto">
          <div className="text-start mb-15!">
            <h2 className="text-2xl md:text-5xl font-semibold">
              What Our
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 ml-2!">Clients Say</span>
            </h2>
            <div className="w-[30vw] lg:w-[10vw] h-1.5 rounded-full bg-gradient-to-l from-cyan-400 to-purple-600 mt-3!" />
          </div>

          <div className="grid  md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 xl:gap-10 gap-6">
            {textTestimonials.map((review, index) => (
              <div
                key={review.id || index}
                className="tr-card"
                style={{
                  "--glow": glowColors[index % glowColors.length],
                }}
              >
                <div className="tr-top-line" />
                <div className="tr-bottom-line" />
                <div className="tr-card-top">
                  <div className="tr-quote">
                    <Quote size={20} />
                  </div>

                  <div className="tr-rating-row">
                    <StarRating rating={review.rating} />
                  </div>

                  <p ref={(el) => (textRefs.current[review.id || index] = el)} className={`tr-text ${expandedCards[review.id || index] ? "expanded" : ""}`}>
                    {review.message}
                  </p>

                  {showButtons[review.id || index] && (
                    <button
                      onClick={() =>
                        setExpandedCards((prev) => ({
                          ...prev,
                          [review.id || index]: !prev[review.id || index],
                        }))
                      }
                      className="tr-see-more"
                    >
                      {expandedCards[review.id || index] ? "See Less" : "See More"}
                    </button>
                  )}
                </div>

                <div className="tr-card-bottom">
                  <div className="tr-rule" />

                  <div className="tr-footer">
                    <div className="tr-user">
                      <div className="tr-avatar flex items-center justify-center relative">
                        {review.image ? <Image src={review.image} alt={review.name} fill className="object-cover" /> : <span className="font-bold">{review.name?.charAt(0)}</span>}
                      </div>
                      <div className="tr-meta">
                        <h4 className="tr-name">{review.name}</h4>
                        <p className="tr-role">{review.designation}</p>
                        <p className="tr-role text-accent! font-bold!">{review.companyName}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="footer-top-accent" aria-hidden="true" />
      <section className="py-5! md:py-18! px-6! bg-background-secondary">
        <div className=" mx-auto">
          <div className="text-start mb-10! md:mb-20!">
            <h2 className="text-2xl md:text-5xl font-semibold">
              Video
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 ml-2!">Stories</span>
            </h2>
            <div className="w-[30vw] lg:w-[10vw] h-1.5 rounded-full bg-gradient-to-l from-cyan-400 to-purple-600 mt-3!" />
          </div>

          <div className="grid md:grid-cols-4 gap-12">
            {textTestimonials
              ?.filter((v) => v.video)
              .map((v) => (
                <div
                  key={v.id}
                  onClick={() => setSelectedVideo(v.video)}
                  className="fade-up group cursor-pointer rounded-3xl overflow-hidden relative border border-[var(--c-border)]"
                >
                  <div className="relative h-96">
                    <video src={v.video} className="w-full h-full object-contain" muted preload="metadata" />

                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition">
                        <Play className="w-10 h-10 text-black ml-1" />
                      </div>
                    </div>

                    <div className="absolute bottom-6 left-6 text-white">
                      <div className="text-xl font-semibold">{v.name}</div>
                      <div className="text-sm opacity-80">{v.designation}</div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {selectedVideo && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center !p-6">
          <div className="relative w-full max-w-5xl scale-100 animate-[fadeIn_.3s_ease]">
            <IoMdCloseCircle onClick={() => setSelectedVideo(null)} className="absolute top-3 right-3  text-white text-3xl cursor-pointer" />
            <iframe width="100%" height="500" src={selectedVideo} title="Video" className="rounded-3xl" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientTestimonials;
